import { useTranslate } from '@tolgee/react'
import React from 'react'

import MenuLogo from '@/components/common/MenuLogo'
import { selectDetailMenuSite } from '@/redux/meta/selector'
import Button from '@/components/common/Button'
import { useSelector } from 'react-redux'
import {
  checkExistNutriGradeTag,
  NutriGradeIcon,
} from 'src/components/icons/NutriGrade'
import useAppContext from 'src/hooks/useAppContext'
import useGetProductImagePath from 'src/hooks/useGetProductImagePath'
import useStoreItemAvailability from 'src/hooks/useStoreItemAvailability'
import {
  getAllProductNutrigradeTags,
  getDisplayPriceFromItem,
  getProductButtonProps,
} from 'src/utils/product'
import { H3, H4 } from '@/components/common/Typo'
import ImageWithLoading from '@/components/common/ImageWithLoading'

const ProductListCard = React.memo(
  ({
    product,
    category,
    showImage = true,
    showDefaultImage = true,
    productImageAspect = 'cover',
    onButtonClick,
  }: {
    product: Oms.MenuItem
    category: Oms.MenuCategory
    showImage: boolean
    showDefaultImage: boolean
    productImageAspect: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down'
    onButtonClick: (params?: any) => void
  }) => {
    const { t } = useTranslate()
    const { formatMoney } = useAppContext()
    const existNutriGradeTag = checkExistNutriGradeTag(product.tags)
    const productNutrigradeTags = getAllProductNutrigradeTags(product.tags)
    const metaState = useSelector(selectDetailMenuSite)
    const { enableTimeLimitUnavailability } = metaState?.features || {}
    const { getProductImagePath, getProductImageBlurDataURL } =
      useGetProductImagePath()

    const {
      text: productButtonText,
      disabled,
      loading,
    } = getProductButtonProps(
      t,
      product,
      category,
      useStoreItemAvailability({ product, category }),
      Boolean(enableTimeLimitUnavailability),
      metaState?.menu?.gmtOffset
    )

    const productImage = getProductImagePath(
      product.imageFullUrls?.[0] || product.imageUrls?.[0],
      showDefaultImage,
      {
        shouldCache: true,
        containerWidth: 250,
        containerHeight: 250,
        objectFit: productImageAspect,
        useUcareCdnScaleCrop: true,
      }
    )

    const { preposition, displayPrice } = getDisplayPriceFromItem(
      t,
      product,
      formatMoney
    )

    return (
      <div className="bg-container block h-full w-full rounded-lg shadow-sm">
        <div className="flex h-full flex-row">
          {showImage && productImage && (
            <div className="relative h-[164px] w-[164px] md:h-[220px] md:w-[220px]">
              {productImage.includes('default-product.png') ? (
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center rounded-l-lg bg-[#f5f8fc]">
                  <MenuLogo className="opacity-20" showStoreName={false} />
                </div>
              ) : (
                <div className="relative h-full w-full">
                  <ImageWithLoading
                    alt={product.name}
                    src={productImage}
                    fill
                    className="rounded-l-lg"
                    blurImageClassName="rounded-l-lg"
                    style={{ objectFit: productImageAspect }}
                    sizes="(max-width: 768px) 164px, 220px"
                    blurDataURL={getProductImageBlurDataURL(
                      product.imageFullUrls?.[0] || product.imageUrls?.[0]
                    )}
                  />
                </div>
              )}
              {existNutriGradeTag &&
                !productImage.includes('default-product.png') && (
                  <div className="absolute bottom-0 w-full bg-[#f2f2f26b] p-1.5 pl-4 text-left text-white transition-all duration-500 md:pl-6">
                    <NutriGradeIcon
                      tags={product.tags}
                      height={50}
                      width={109}
                    />
                  </div>
                )}
            </div>
          )}
          <div className="bg-container flex flex-1 flex-col justify-between rounded-br-lg rounded-tr-lg p-4">
            <div>
              <H3 className="text-headings mb-2 line-clamp-2 text-lg font-bold md:line-clamp-3">
                {product.name}
              </H3>
              <H4 className="mb-4 text-base font-medium">
                {preposition ? `${preposition} ` : ''}
                {displayPrice}
              </H4>
              {(!showImage ||
                !productImage ||
                productImage.includes('default-product.png')) &&
                (product?.tags?.length || 0) > 0 && (
                  <div className="mt-2">
                    <NutriGradeIcon
                      tags={product.tags}
                      height={40}
                      width={79}
                    />
                  </div>
                )}
              {(productNutrigradeTags?.length || 0) > 0 && (
                <div className="flex flex-wrap gap-1.5 py-2.5">
                  {productNutrigradeTags?.map((tag) => (
                    <div
                      key={tag}
                      className="font-roboto inline-block rounded border border-gray-300 bg-white px-1.5 py-1 text-sm text-black"
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                className="z-1 min-w-[110px] text-base font-bold"
                disabled={disabled || loading}
                onClick={onButtonClick}
                isLoading={loading}
              >
                {productButtonText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

ProductListCard.displayName = 'ProductListCard'

export default ProductListCard
